import React, { useState, useEffect, useRef, useReducer } from 'react';
import JobItem from './JobItem';
import { JobData } from '../../api/JobsService';

interface ApiJobsListProps {
  jobs: JobData[];
  handleJobClick: (jobId: number) => void;
  handleActionClick: (jobId?: number) => void;
  getActionButtonText: (jobId: number) => string;
  getActionButtonVariant: (jobId: number) => 'outline-primary' | 'outline-danger' | 'transparent';
  associatingJobs?: Set<number>;
  relationshipManagerEmail?: string | null;
}

// Simple reducer that just increments the counter on each action
const forceUpdateReducer = (state: number, action: 'increment'): number => {
  return state + 1;
};

const ApiJobsList: React.FC<ApiJobsListProps> = ({
  jobs,
  handleJobClick,
  handleActionClick,
  getActionButtonText,
  getActionButtonVariant,
  associatingJobs = new Set(),
  relationshipManagerEmail
}) => {
  // Keep track of previously processing jobs
  const processingJobsRef = useRef<Set<number>>(new Set());
  
  // Force a re-render when a job's processing state changes
  const [forceUpdateCounter, dispatchForceUpdate] = useReducer(forceUpdateReducer, 0);
  
  useEffect(() => {
    // Compare current associatingJobs with previous state
    const currentProcessingJobs = new Set(associatingJobs);
    const previousProcessingJobs = new Set(processingJobsRef.current);
    
    // Check if any jobs changed state
    let stateChanged = false;
    
    // Check for newly processing jobs
    Array.from(currentProcessingJobs).forEach(jobId => {
      if (!previousProcessingJobs.has(jobId)) {
        stateChanged = true;
      }
    });
    
    // Check for jobs that finished processing
    Array.from(previousProcessingJobs).forEach(jobId => {
      if (!currentProcessingJobs.has(jobId)) {
        stateChanged = true;
      }
    });
    
    // Update ref for next comparison
    processingJobsRef.current = currentProcessingJobs;
    
    // Force re-render if state changed
    if (stateChanged) {
      dispatchForceUpdate('increment');
    }
  }, [associatingJobs]);
  
  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column',
      gap: '1rem',
      marginTop: '1rem',
      textAlign: 'left'
    }}>
      {jobs.map((job) => (
        <JobItem
          key={`${job.id}-${associatingJobs.has(job.id) ? `processing-${forceUpdateCounter}` : 'idle'}`}
          jobId={job.id}
          title={job.title}
          location={job.locations && job.locations.length > 0 
            ? `${job.locations[0].city}, ${job.locations[0].region}` 
            : 'Location not specified'}
          isActive={job.status === 'Open'}
          onJobClick={() => handleJobClick(job.id)}
          onActionClick={handleActionClick}
          actionButtonText={getActionButtonText(job.id)}
          actionButtonVariant={getActionButtonVariant(job.id)}
          easyApplyEnabled={job.easyApplyEnabled}
          relationshipManagerEmail={relationshipManagerEmail}
        />
      ))}
    </div>
  );
};

export default ApiJobsList; 